import React from "react";

const VideoPerformanceGrid = ({ videos, compact = false }) => {
  const getLikesLevel = (video) => {
    const likes = video.video?.count_likes || 0;
    if (likes >= 100000) return '100K+';
    if (likes >= 10000) return '10K+';
    if (likes >= 1000) return '1K+';
    return '<1K';
  };

  const getCommentsLevel = (video) => {
    const comments = video.video?.count_comments || 0;
    if (comments >= 1000) return '1K+';
    if (comments >= 100) return '100+';
    if (comments >= 10) return '10+';
    return '<10';
  };

  const likesLevels = ['100K+', '10K+', '1K+', '<1K'];
  const commentsLevels = ['<10', '10+', '100+', '1K+']; // low to high (x-axis)

  const grid = {};
  likesLevels.forEach(l => {
    grid[l] = {};
    commentsLevels.forEach(c => {
      grid[l][c] = 0;
    });
  });

  videos.forEach((v) => {
    const likesLevel = getLikesLevel(v);
    const commentsLevel = getCommentsLevel(v);
    grid[likesLevel][commentsLevel]++;
  });

  const maxCount = Math.max(
    ...Object.values(grid).flatMap(g => Object.values(g))
  );

  const labelClass = compact ? "w-12 text-[10px]" : "w-20 text-xs";
  const cellClass = compact
    ? "flex-1 aspect-square rounded flex items-center justify-center text-[10px] font-medium transition-all min-w-0"
    : "flex-1 aspect-square rounded-lg flex items-center justify-center text-xs font-medium transition-all";
  const textSizeClass = compact ? "text-[10px]" : "text-xs";

  return (
    <div className="overflow-x-auto">
      <div className={compact ? "min-w-0 max-w-[240px] mx-auto" : "min-w-[300px]"}>
        {/* Top row: "Likes" label top-left, empty cells for alignment */}
        <div className={`flex items-center gap-0.5 ${compact ? "mb-0.5" : "mb-1"}`}>
          <div className={`${labelClass} text-theme-text opacity-60 font-medium pr-1 text-right`}>
            Likes
          </div>
          {commentsLevels.map((_, i) => (
            <div key={`empty-${i}`} className={cellClass} style={{ backgroundColor: 'transparent' }} />
          ))}
        </div>
        {/* Grid rows: Y-axis labels (likes) on left, cells on right */}
        {likesLevels.map((likes) => (
          <div key={likes} className={`flex items-center gap-0.5 ${compact ? "mb-0.5" : "mb-1"}`}>
            <div className={`${labelClass} text-theme-text opacity-60 font-medium pr-1 text-right`}>
              {likes}
            </div>
            {commentsLevels.map((comments) => {
              const count = grid[likes][comments];
              const intensity = maxCount > 0 ? count / maxCount : 0;
              return (
                <div
                  key={comments}
                  className={cellClass}
                  style={{
                    backgroundColor: count > 0
                      ? `rgba(59, 130, 246, ${0.15 + intensity * 0.7})`
                      : 'var(--color-theme-main)',
                    color: intensity > 0.5 ? 'white' : 'var(--color-theme-text)',
                  }}
                  title={`${likes} likes, ${comments} comments: ${count} videos`}
                >
                  {count > 0 ? count : ''}
                </div>
              );
            })}
          </div>
        ))}
        {/* Bottom row: X-axis (comments) labels, "Comments" label bottom-right */}
        <div className={`flex items-center gap-0.5 ${compact ? "mt-0.5" : "mt-1"}`}>
          <div className={`${labelClass} pr-1 flex-shrink-0`} aria-hidden="true" />
          {commentsLevels.map((c) => (
            <div key={c} className={`flex-1 text-center text-theme-text opacity-60 font-medium ${textSizeClass}`}>
              {c}
            </div>
          ))}
          <div className={`${labelClass} flex-shrink-0 text-theme-text opacity-40 pl-1 text-right ${textSizeClass}`}>
            Comments →
          </div>
        </div>
      </div>
    </div>
  );
};

export default VideoPerformanceGrid;
